import React, { useState } from "react";
import { contactTabs, services } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitQuoteRequest } from "../lib/api";
import { fmtDate, fmtDateSv } from "../lib/date";
import ContactInfo from "./ContactInfo";
import BookingPicker from "./BookingPicker";
import { CAMPAIGN_LABEL } from "../lib/promotion";

export default function Contact({ initialTab = 0 }) {
  const { toast } = useToast();
  const [tab, setTab] = useState(initialTab);
  const [service, setService] = useState("");
  const [agree, setAgree] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");

  const isBooking = Boolean(date && time);

  const buildPayload = (form) => ({
    ...Object.fromEntries(new FormData(form).entries()),
    service,
    segment: contactTabs[tab],
    type: isBooking ? "booking" : "quote",
    promotion: CAMPAIGN_LABEL,
    preferredDate: date ? fmtDate(date) : null,
    preferredTime: time || null,
  });

  const resetForm = (form) => {
    form.reset();
    setService(""); setAgree(false); setDate(null); setTime("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!service) {
      toast({ title: "Välj en tjänst", description: "Ange vilken tjänst du är intresserad av." });
      return;
    }
    if (!agree) {
      toast({ title: "Godkänn integritetspolicyn", description: "Du måste godkänna policyn för att skicka." });
      return;
    }
    const form = e.target;
    const payload = buildPayload(form);
    setLoading(true);
    try {
      await submitQuoteRequest(payload);
      const desc = isBooking ? `Önskad tid: ${fmtDateSv(date)} kl ${time}` : "Din förfrågan har tagits emot.";
      toast({ title: "Tack! Vi hör av oss inom en arbetsdag.", description: desc });
      resetForm(form);
      setSent(true);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Något gick fel",
        description: "Kunde inte skicka just nu. Försök igen eller ring oss direkt.",
      });
    } finally {
      setLoading(false);
    }
  };

  let submitLabel = isBooking ? "Boka besiktning" : "Skicka förfrågan";
  if (loading) {
    submitLabel = null; // spinner shown instead
  }

  return (
    <section id="kontakt" className="relative hpr-gradient py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-12 items-start">
        <ContactInfo />

        {/* form card */}
        <div className="rounded-3xl bg-white light-card p-5 sm:p-8 shadow-2xl">
          <div className="flex flex-wrap gap-2 rounded-xl bg-slate-100 p-1.5 mb-6">
            {contactTabs.map((label, i) => (
              <button key={label} onClick={() => setTab(i)} type="button" data-testid={`contact-tab-${i}`}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${tab === i ? "bg-[#1B45C4] text-white shadow" : "text-slate-600 hover:bg-white"}`}>
                {label}
              </button>
            ))}
          </div>

          {sent ? (
            <div className="flex flex-col items-center text-center py-10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#1B45C4]/10"><CheckCircle2 className="w-9 h-9 text-[#1B45C4]" /></div>
              <h3 className="mt-4 text-xl font-bold text-slate-900">Tack för din förfrågan!</h3>
              <p className="mt-2 text-slate-600">Vi hör av oss inom en arbetsdag.</p>
              <Button onClick={() => setSent(false)} className="mt-6 rounded-full bg-[#1B45C4] hover:bg-[#12308f] text-white" data-testid="contact-send-another">Skicka en till</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="fn">Förnamn*</Label><Input id="fn" name="firstName" required className="mt-1.5 h-11" data-testid="contact-first-name" /></div>
                <div><Label htmlFor="ln">Efternamn*</Label><Input id="ln" name="lastName" required className="mt-1.5 h-11" data-testid="contact-last-name" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="em">E-post*</Label><Input id="em" type="email" name="email" required className="mt-1.5 h-11" data-testid="contact-email" /></div>
                <div><Label htmlFor="ph">Telefon*</Label><Input id="ph" name="phone" required className="mt-1.5 h-11" data-testid="contact-phone" /></div>
              </div>
              <div><Label htmlFor="addr">Gatuadress*</Label><Input id="addr" name="address" required className="mt-1.5 h-11" data-testid="contact-address" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label htmlFor="zip">Postnummer*</Label><Input id="zip" name="zip" required className="mt-1.5 h-11" data-testid="contact-zip" /></div>
                <div><Label htmlFor="city">Ort*</Label><Input id="city" name="city" required className="mt-1.5 h-11" data-testid="contact-city" /></div>
              </div>
              <div>
                <Label>Vilken tjänst är du intresserad av?*</Label>
                <Select value={service} onValueChange={setService} required>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="contact-service-select"><SelectValue placeholder="Välj tjänst" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>)}
                    <SelectItem value="Flera tjänster">Flera tjänster</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="msg">Meddelande</Label><Textarea id="msg" name="message" rows={3} className="mt-1.5" placeholder="Berätta gärna om ditt tak..." data-testid="contact-message" /></div>

              <BookingPicker date={date} setDate={setDate} time={time} setTime={setTime} />

              <div className="flex items-start gap-3">
                <Checkbox id="agree" checked={agree} onCheckedChange={setAgree} className="mt-0.5" data-testid="contact-consent" />
                <Label htmlFor="agree" className="text-sm text-slate-600 font-normal cursor-pointer">Jag godkänner integritetspolicyn.*</Label>
              </div>
              <p className="rounded-xl bg-[#38BDF8]/10 px-3 py-2 text-center text-xs font-semibold text-[#1B45C4]" data-testid="contact-campaign-note">20% kampanjrabatt markeras på din förfrågan.</p>
              <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="w-full rounded-full h-13 py-3 bg-[#1B45C4] hover:bg-[#12308f] text-white font-semibold text-base">
                {loading ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Skickar...</span> : submitLabel}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
